// Reference-data family: SEU categories, energy sources, energy prices (tariffs), customer settings, fiscal years, users.
// Reshape only. Ids are NomOS record ids (the Sentinel uuids of these lookup rows are not carried in NomOS).
import type { CustomerSettings, EnergyPrice, EnergySource, FiscalYear, SEUCategory, SeuCategory, UserListItem } from '@/types';
import { getConfig, nomosFetch, queryRecords, refs, scalar, zulu, type NomosFilter } from '../client';
import type { AnyFn, NomosRegistry } from '../registry';
import { countryCodes } from './lookups';

const str = (x: unknown) => (typeof x === 'string' ? x : null);
const num = (x: unknown) => (typeof x === 'number' ? x : typeof x === 'string' && x !== '' ? Number(x) : null);

// Lookup rows keep their Sentinel id (sentinel_*_id), colour and icon so the UI is unchanged.
async function seuRows() {
  const res = await queryRecords('seu_category', { fields: ['code', 'name', 'description', 'display_order', 'sentinel_color', 'sentinel_icon', 'sentinel_seu_category_id'], limit: 200 });
  return res.rows.map((r) => ({ id: str(scalar(r.values.sentinel_seu_category_id)) ?? r.record_id, code: scalar(r.values.code) as string,
    name: (scalar(r.values.name) as string) ?? r.kenning, color: str(scalar(r.values.sentinel_color)), icon: str(scalar(r.values.sentinel_icon)),
    display_order: num(scalar(r.values.display_order)), description: str(scalar(r.values.description)) }))
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
}
async function getSEUCategories(): Promise<SEUCategory[]> { return (await seuRows()) as unknown as SEUCategory[]; }
async function getSeuCategories(): Promise<SeuCategory[]> { return (await seuRows()) as unknown as SeuCategory[]; }

const ES_FIELDS = ['code', 'name', 'unit', 'co2_factor_kg_per_kwh', 'kwh_conversion_factor', 'description', 'sentinel_color', 'sentinel_icon', 'sentinel_energy_source_id', 'sentinel_name'];
async function energySourceRows() {
  const res = await queryRecords('energy_source', { fields: ES_FIELDS, limit: 200 });
  return res.rows;
}
/** record_id <-> Sentinel energy_source id (tariff rows reference the NomOS record). */
async function energySourceIds(): Promise<{ toSentinel: Map<string, string>; toRecord: Map<string, string> }> {
  const toSentinel = new Map<string, string>(); const toRecord = new Map<string, string>();
  for (const r of await energySourceRows()) {
    const sid = str(scalar(r.values.sentinel_energy_source_id)) ?? r.record_id;
    toSentinel.set(r.record_id, sid); toRecord.set(sid, r.record_id);
  }
  return { toSentinel, toRecord };
}
async function getEnergySources(): Promise<EnergySource[]> {
  return (await energySourceRows()).map((r) => ({ id: str(scalar(r.values.sentinel_energy_source_id)) ?? r.record_id, code: scalar(r.values.code),
    name: str(scalar(r.values.sentinel_name)) ?? (scalar(r.values.name) as string) ?? r.kenning, unit: str(scalar(r.values.unit)), co2_factor_kg_per_kwh: num(scalar(r.values.co2_factor_kg_per_kwh)),
    kwh_conversion_factor: num(scalar(r.values.kwh_conversion_factor)), description: str(scalar(r.values.description)),
    color: str(scalar(r.values.sentinel_color)), icon: str(scalar(r.values.sentinel_icon)) })) as unknown as EnergySource[];
}

/** Tariffs -> Sentinel energy prices. Filters map to country_ref / energy_source references. */
async function getEnergyPrices(params?: { country_code?: string; energy_source_id?: string }): Promise<EnergyPrice[]> {
  // ONE round trip: the country is named through the reference path (country_ref.code), so the tariffs need no
  // country lookup first; the vocabularies travel beside them.
  const esP = energySourceIds();
  const clauses: NomosFilter[] = [];
  if (params?.country_code) clauses.push({ attribute: 'country_ref.code', op: 'eq', value: params.country_code.toUpperCase() });
  if (params?.energy_source_id) {
    // a Sentinel energy-source id maps through the vocabulary; a NomOS record id is used as is
    const es = await esP;
    clauses.push({ attribute: 'energy_source', op: 'eq', value: es.toRecord.get(params.energy_source_id) ?? params.energy_source_id });
  }
  const out: EnergyPrice[] = [];
  for (let offset = 0; ; offset += 500) {
    const [res, es, countries] = await Promise.all([
      queryRecords('tariff', { filter: clauses.length === 1 ? clauses[0] : clauses.length ? { and: clauses } : undefined,
        fields: ['country_ref', 'energy_source', 'price_per_kwh', 'currency', 'valid_from', 'valid_to', 'source', 'sentinel_tariff_id'], limit: 500, cursor: offset,
        sorts: [{ field: 'valid_from', direction: 'desc' }] }),
      esP, countryCodes()]);
    for (const r of res.rows) {
      const v = r.values; const c = refs(v.country_ref)[0]; const e = refs(v.energy_source)[0];
      out.push({ id: str(scalar(v.sentinel_tariff_id)) ?? r.record_id, country_code: c ? countries.get(c.id) : null, energy_source_id: (e && es.toSentinel.get(e.id)) ?? e?.id ?? null, energy_source_name: e?.name ?? null,
        price_per_kwh: num(scalar(v.price_per_kwh)), currency: str(scalar(v.currency)), valid_from: str(scalar(v.valid_from)),
        valid_to: str(scalar(v.valid_to)), source: str(scalar(v.source)) } as unknown as EnergyPrice);
    }
    if (out.length >= res.total || res.rows.length === 0) break;
  }
  return out;
}

async function tenantFiscalStartMonth(): Promise<number> {
  const res = await queryRecords('tenant', { fields: ['fiscal_year_start_month'], limit: 1 });
  return num(scalar(res.rows[0]?.values.fiscal_year_start_month)) ?? 1;
}
export { tenantFiscalStartMonth };

async function getCustomerSettings(): Promise<CustomerSettings> {
  return { fiscal_year_start_month: await tenantFiscalStartMonth() };
}

/**
 * GAP: NomOS has no fiscal calendar. Boundaries are derived from tenant.fiscal_year_start_month with Sentinel's
 * verified-data window (FY25-FY26, routers/customers.py MIN/MAX_TRUSTWORTHY_FISCAL_YEAR). Calendar arithmetic only.
 */
async function getFiscalYears(): Promise<FiscalYear[]> {
  const m = await tenantFiscalStartMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  const lastDay = (y: number, mon: number) => new Date(Date.UTC(y, mon, 0)).getUTCDate();
  return [2026, 2025].map((fy) => {
    // FY label = calendar year in which the fiscal year ends (m=1 -> calendar year)
    const startYear = m === 1 ? fy : fy - 1;
    const endMonth = m === 1 ? 12 : m - 1;
    const endYear = m === 1 ? fy : fy;
    return { fiscal_year: fy, start_date: `${startYear}-${pad(m)}-01`, end_date: `${endYear}-${pad(endMonth)}-${lastDay(endYear, endMonth)}`,
      label: `FY${pad(fy % 100)}` };
  });
}

/**
 * GET /tenants/{t}/members (unversioned route) -> user list. The agent member (no role_code) is not a user.
 * An admin gets every member; a non-admin gets their own row plus a NAME-ONLY directory of the people who share
 * their site scope (no email, role or login fields - those rows are people already; the server leaves agents out).
 * That is what the assignee pickers need; Sentinel gave a non-admin nothing (GET /users was admin-only).
 */
async function getUsers(params?: { role?: string; is_active?: boolean; search?: string; page?: number; page_size?: number }): Promise<UserListItem[]> {
  type M = { member_id: string; email?: string; first_name?: string | null; last_name?: string | null; display_name?: string | null; is_active?: boolean;
    last_login_at?: string | null; created_at?: string; role_code?: string | null; role_name?: string | null; sentinel_user_id?: string | null;
    is_person?: boolean; assigned_sites_count?: number; assigned_countries_count?: number };
  const res = await nomosFetch<M[]>('GET', `/tenants/${encodeURIComponent(getConfig().tenant)}/members`, undefined, { tenantScoped: false });
  const directory = (m: M) => m.email === undefined;
  // every member holds a role (the agent: the 'agent' service role); the server says who is a person
  let rows = res.filter((m) => (m.is_person ?? !!m.role_code) || directory(m));
  if (params?.role) rows = rows.filter((m) => m.role_code === params.role);
  if (params?.is_active !== undefined) rows = rows.filter((m) => (m.is_active ?? true) === params.is_active);
  if (params?.search) { const q = params.search.toLowerCase(); rows = rows.filter((m) => `${m.display_name ?? ''} ${m.first_name ?? ''} ${m.last_name ?? ''} ${m.email ?? ''}`.toLowerCase().includes(q)); }
  if (params?.page_size) { const p = params.page ?? 1; rows = rows.slice((p - 1) * params.page_size, p * params.page_size); }
  return rows.map((m) => ({ id: m.sentinel_user_id ?? m.member_id, email: m.email ?? '', first_name: m.first_name ?? null, last_name: m.last_name ?? null,
    display_name: m.display_name ?? m.email ?? '', role_code: m.role_code, role_name: m.role_name ?? null, is_active: m.is_active ?? true,
    last_login_at: zulu(m.last_login_at) ?? null, created_at: zulu(m.created_at), assigned_buildings_count: m.assigned_sites_count ?? 0, assigned_countries_count: m.assigned_countries_count ?? 0 })) as unknown as UserListItem[];
}

export const referenceImpl: NomosRegistry = {
  getSEUCategories: getSEUCategories as unknown as AnyFn,
  getSeuCategories: getSeuCategories as unknown as AnyFn,
  getEnergySources: getEnergySources as unknown as AnyFn,
  getEnergyPrices: getEnergyPrices as unknown as AnyFn,
  getCustomerSettings: getCustomerSettings as unknown as AnyFn,
  getFiscalYears: getFiscalYears as unknown as AnyFn,
  getUsers: getUsers as unknown as AnyFn,
};
